import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  createIncentive,
  createIncentiveFailure,
  createIncentiveSuccess,
  getAgents,
  getAgentsFailure,
  getAgentsSuccess,
  getIncentiveById,
  getIncentiveByIdFailure,
  getIncentiveByIdSuccess,
  getIncentives,
  getIncentivesFailure,
  getIncentivesSuccess,
  updateIncentive,
} from 'redux/slice/incentive/Incentive.slice';
import {
  AgentsResponse,
  CreateIncentiveRequest,
  CreateIncentiveResponse,
  IncentivesResponse,
  SingleIncentiveResponse,
  UpdateIncentiveRequest,
} from 'redux/types/Incentive.types';
import { incentiveEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* handleGetIncentives() {
  try {
    const response: IncentivesResponse = yield call(
      HTTPClient.GetRequest,
      incentiveEndPoints.GET_INCENTIVES
    );
    const transformedData = response?.data?.data?.map((item) => ({
      programName: item.incentiveName,
      allocator: item.lobDetails[0]?.allocatorId || '',
      product: item.lobDetails.map((lob) => lob.product).join(', '),
      bucket: item.lobDetails.map((lob) => lob.bucket).join(', '),
      criterias: item.programType
        .map((type: string) => {
          if (type.startsWith('variable')) return 'Variable';
          if (type.startsWith('milestone')) return 'Milestone';
          return type.charAt(0).toUpperCase() + type.slice(1);
        })
        .filter(
          (value: string, index: number, self: string[]) =>
            self.indexOf(value) === index
        ),
      id: item.id,
    }));
    yield put(getIncentivesSuccess(transformedData));
  } catch (error) {
    yield put(getIncentivesFailure('Error while fetching incentives'));
    message.error('Failed to fetch incentives');
  }
}

function* handleCreateIncentive(action: PayloadAction<CreateIncentiveRequest>) {
  try {
    const response: CreateIncentiveResponse = yield call(
      HTTPClient.PostRequest,
      incentiveEndPoints.CREATE_INCENTIVE,
      action.payload
    );
    yield put(createIncentiveSuccess(response));
    message.success('Incentive created successfully');
  } catch (error) {
    yield put(createIncentiveFailure('Error while creating incentive'));
    message.error('Failed to create incentive');
  }
}

function* handleGetAgents() {
  try {
    const response: AgentsResponse = yield call(
      HTTPClient.GetRequest,
      incentiveEndPoints.GET_AGENTS
    );
    const transformedAgents =
      response?.data?.data?.map((agent) => ({
        agentId: agent.agentId,
        agentName: agent.agentName,
      })) || [];
    yield put(getAgentsSuccess(transformedAgents));
  } catch (error) {
    yield put(getAgentsFailure('Error while fetching agents'));
    message.error('Failed to fetch agents');
  }
}

export function* getIncentiveByIdSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    const response = yield call(
      HTTPClient.GetRequest,
      incentiveEndPoints.GET_INCENTIVE_BY_ID(action.payload)
    );
    const typedResponse = response as SingleIncentiveResponse;
    yield put(getIncentiveByIdSuccess(typedResponse.data.data));
  } catch (error: any) {
    yield put(getIncentiveByIdFailure(error.message));
  }
}

export function* updateIncentiveById(
  action: PayloadAction<UpdateIncentiveRequest>
) {
  try {
    const response: CreateIncentiveResponse = yield call(
      HTTPClient.PutRequest,
      incentiveEndPoints.UPDATE_INCENTIVE_BY_ID(action.payload.id),
      action.payload
    );
    yield put(createIncentiveSuccess(response));
    message.success('Incentive updated successfully');
  } catch (error) {
    yield put(createIncentiveFailure('Error while creating incentive'));
    message.error('Failed to update incentive');
  }
}

export function* watchIncentiveSaga() {
  yield all([
    takeLatest(getIncentives.type, handleGetIncentives),
    takeLatest(createIncentive.type, handleCreateIncentive),
    takeLatest(getIncentiveById.type, getIncentiveByIdSaga),
    takeLatest(getAgents.type, handleGetAgents),
    takeLatest(updateIncentive.type, updateIncentiveById),
  ]);
}
